import { Carousel as MantineCarousel } from '@mantine/carousel'
import {
  Anchor,
  Box,
  type BoxProps,
  Flex,
  type MantineColor,
  rem,
  Title,
} from '@mantine/core'
import clsx from 'clsx'
import Link from 'next/link'
import { type PropsWithChildren } from 'react'

import { useMediaQuery } from '@flecto/ui'

import { Loading } from './Loading'
import styles from './styles.module.css'

export type CarouselProps = PropsWithChildren<BoxProps> & {
  title?: string
  titleColor?: MantineColor
  linkLabel?: string
  linkHref?: string
  variant?: 'card' | 'default'
}

export function Carousel({
  title,
  titleColor,
  linkLabel,
  linkHref,
  variant = 'default',
  children,
  ...props
}: CarouselProps) {
  const { isMobile } = useMediaQuery()
  const classes = clsx(styles.carousel, props.className, styles[variant])

  return (
    <Box {...props} className={classes}>
      {(title || linkLabel) && (
        <Flex align='center' gap='md' mb='md'>
          {title && (
            <Title order={4} lh={1} c={titleColor}>
              {title}
            </Title>
          )}
          {linkLabel && (
            <Anchor component={Link} href={linkHref ?? '#'}>
              {linkLabel}
            </Anchor>
          )}
        </Flex>
      )}
      <MantineCarousel
        withIndicators={!isMobile}
        slideSize={isMobile ? '50%' : '20%'}
        slideGap={variant === 'default' ? 'md' : 'lg'}
        align='start'
        slidesToScroll={isMobile ? 2 : 5}
        controlSize={40}
        controlsOffset={0}
        // @ts-expect-error Mantine V7 types
        styles={(theme) => ({
          control: {
            '&[data-inactive]': {
              opacity: 0,
              cursor: 'default',
            },
          },
          indicator: {
            backgroundColor: theme.lineHeights,
            borderColor:
              theme.colors[theme.primaryColor][theme.primaryShade as number],
            width: rem(12),
            height: rem(4),
            transition: 'width 250ms ease',

            '&[data-active]': {
              width: rem(40),
            },
          },
        })}
      >
        {children}
      </MantineCarousel>
    </Box>
  )
}

Carousel.Slide = MantineCarousel.Slide
Carousel.Loading = Loading
